import React, { useMemo } from 'react';

import classnames from 'classnames';

import LinkButton from 'components/uiLibrary/LinkButton';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import { COMPONENTS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import EntityName from 'components/Globals/EntityName';

import { TP } from 'constants/index';

import { formatGenres, formatProfessions, formatEmployment } from 'utils/text';
import { getOrganizationLocation } from 'utils/globals';
import useNavigate from 'utils/hooks/useNavigate';

import { useTranslation } from 'src/i18n';

import { ENTITY_TYPES } from 'operabase-router/constants';

import classes from './SearchResultCard.module.scss';

const Card = ({ entity, entityType, isSelected, className, typographyProps = {}, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  
  const { title, employment, professions, genres, location, organizationType, birthDeathInfo, subTitle, subText } = useMemo(() => {
    if (!entity) {
      return {};
    }

    switch (entityType) {
      case ENTITY_TYPES.PROFILE: {
        // New 4-line profile structure + optional 5th line for deceased
        const formattedEmployment = formatEmployment(entity?.organizations, 48); // 3x wider than PreviewCard
        const formattedProfessions = formatProfessions(entity?.professions, 48);
        const formattedGenres = formatGenres(entity?.workTypes, 48);
        
        // Birth-death info for deceased people (5th line) - only show when both years available
        const { birth: { year: birthYear } = {}, death: { year: deathYear } = {} } = entity?.personal || {};
        const birthDeathDisplay = birthYear && deathYear ? `${birthYear}-${deathYear}` : '';

        return {
          title: entity?.name,
          employment: formattedEmployment,
          professions: formattedProfessions,
          genres: formattedGenres,
          birthDeathInfo: birthDeathDisplay,
        };
      }
      case ENTITY_TYPES.ORGANIZATION: {
        // New 4-line organization structure
        const formattedGenres = formatGenres(entity?.workTypes, 48);
        const locationInfo = getOrganizationLocation(entity);

        return {
          title: entity?.name,
          location: locationInfo,
          organizationType: entity?.organizationType?.name,
          genres: formattedGenres,
        };
      }
      case ENTITY_TYPES.WORK: {
        // Keep existing work structure for now
        const workName = entity?.original_name || entity?.name || '';
        const translatedName = entity?.name === entity?.original_name ? '' : entity?.name;

        return {
          title: `${workName} ${translatedName ? `(${translatedName})` : ''}`,
          subTitle: entity?.composer?.name,
          subText: entity?.workType?.name || '',
        };
      }
      case ENTITY_TYPES.CITY:
      case ENTITY_TYPES.COUNTRY:
      case ENTITY_TYPES.REGION: {
        // Keep existing location structure for now
        const isCity = entityType === ENTITY_TYPES.CITY;

        const getLocationLabel = () => {
          if (isCity) {
            return [entity?.state?.name, entity?.country?.name].filter(Boolean).join(', ');
          }

          return t(entityType === ENTITY_TYPES.REGION ? `${TP}.FN_REGION` : `${TP}.m_COUNTRY`);
        };

        return {
          title: entity?.name,
          subTitle: getLocationLabel(),
          subText: isCity ? t(`${TP}.m_CITY`) : '',
        };
      }
      case ENTITY_TYPES.AGENCY: {
        // Keep existing agency structure for now
        return {
          title: entity?.name,
          subTitle: entity?.country?.name || t(`${TP}.M_AGENCY`),
        };
      }
      default: {
        return {
          title: entity?.name,
        };
      }
    }
  }, [entity, entityType, t]);
  
  const { title: titleProps = {} } = typographyProps;
  const { size = 12, weight = 'medium', color = 'primary' } = titleProps;

  return (
    <div className={classnames(classes.card, { [classes.card__selected]: isSelected }, className)}>
      {/* Line 1: Title/Name */}
      <Typography size={size} weight={weight} color={color} className={classes.title}>
        {[ENTITY_TYPES.WORK, ENTITY_TYPES.ORGANIZATION, ENTITY_TYPES.PROFILE].includes(entityType) ? (
          <EntityName
            entity={{
              ...entity,
              name: title,
            }}
            entityType={entityType}
            trackingData={{
              ...trackingData,
              component: COMPONENTS.SEARCH_RESULT_CARD,
            }}
            isRaw
          />
        ) : (
          title
        )}
      </Typography>

      {/* Profile 4-line structure + optional 5th line */}
      {entityType === ENTITY_TYPES.PROFILE && employment && (
        <Typography size={10} color="secondary" className={classes.employment}>
          {employment}
        </Typography>
      )}
      {entityType === ENTITY_TYPES.PROFILE && professions && (
        <Typography size={10} color="secondary" className={classes.professions}>
          {professions}
        </Typography>
      )}
      {entityType === ENTITY_TYPES.PROFILE && genres && (
        <Typography size={9} color="secondary" className={classes.genres} textTransform="uppercase">
          {genres}
        </Typography>
      )}
      {entityType === ENTITY_TYPES.PROFILE && birthDeathInfo && (
        <Typography size={10} color="secondary" className={classes.birthDeathInfo}>
          {birthDeathInfo}
        </Typography>
      )}

      {/* Organization 4-line structure */}
      {entityType === ENTITY_TYPES.ORGANIZATION && location && (
        <Typography size={10} color="secondary" className={classes.location}>
          {location}
        </Typography>
      )}
      {entityType === ENTITY_TYPES.ORGANIZATION && organizationType && (
        <Typography size={10} color="secondary" className={classes.organizationType}>
          {organizationType}
        </Typography>
      )}
      {entityType === ENTITY_TYPES.ORGANIZATION && genres && (
        <Typography size={9} color="secondary" className={classes.genres} textTransform="uppercase">
          {genres}
        </Typography>
      )}

      {/* Legacy structure for other entity types */}
      {![ENTITY_TYPES.PROFILE, ENTITY_TYPES.ORGANIZATION].includes(entityType) && [subTitle, subText].map(
        (text, index) =>
          text && (
            <Typography key={index} size="11" color="secondary">
              {entityType === ENTITY_TYPES.WORK && index === 0 ? (
                <EntityName
                  entityType={ENTITY_TYPES.PROFILE}
                  entity={{
                    ...entity,
                    name: text,
                  }}
                  trackingData={{
                    ...trackingData,
                    component: COMPONENTS.SEARCH_RESULT_CARD,
                  }}
                  isRaw
                />
              ) : (
                text
              )}
            </Typography>
          ),
      )}
    </div>
  );
};

const SearchResultCard = ({
  entityType,
  entity,
  isSelected,
  isLink,
  className,
  typographyProps,
  onClose,
  trackingData = {},
}) => {
  const navigate = useNavigate();

  if (isLink) {
    const linkProps = navigate.getLinkProps({
      entityType: entityType === ENTITY_TYPES.PROFILE ? ENTITY_TYPES.PROFILE : entityType,
      entity,
      onlyLinkProps: true,
    });

    return (
      <LinkButton
        styles={{ root: classes.linkCard }}
        {...linkProps}
        isLink
        variant="native"
        {...(onClose && { onClick: onClose })}
        trackingData={{
          ...trackingData,
          component: COMPONENTS.SEARCH_RESULT_CARD,
          subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
          meta: {
            entityType,
            entityId: entity?.id,
          },
        }}
      >
        <Card
          entityType={entityType}
          entity={entity}
          isLink={isLink}
          isSelected={isSelected}
          className={className}
          trackingData={trackingData}
        />
        <SpriteIcon icon="chevron_right" size="14" />
      </LinkButton>
    );
  }

  return (
    <Card
      entityType={entityType}
      entity={entity}
      isSelected={isSelected}
      className={className}
      typographyProps={typographyProps}
      trackingData={trackingData}
    />
  );
};

export default SearchResultCard;
